if (!customElements.get('investor-notices')) {
  customElements.define(
    'investor-notices',
    class InvestorNotices extends HTMLElement {
      constructor() {
        super();
        this.currentPage = 1;
        this.isLoading = false;

        // Mobile virtual list state
        this.allItems = [];
        this.itemHeights = [];
        this._prefix = [0];
        this.renderStart = -1;
        this.windowSize = 10; // always render at most 10 rows
        this.overscan = 2; // rows kept above the first visible one for smoother up-scroll
        this.estimatedRowHeight = 80;
        this._rafId = null;
        this._needsRender = false;
        this._virtualActive = false;

        // Mobile incremental loading: fetch pages on demand as the user scrolls
        this.mobilePageSize = 20;
        this.mobileNextPage = 1;
        this.mobileHasMore = true;
        this.isFetchingMore = false;
        this.loadMoreThreshold = 5; // begin fetching when this many rows from the end

        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onPageClick = this.onPageClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
      }

      connectedCallback() {
        this.listEl = this.querySelector('[data-investor-list]');
        this.paginationEl = this.querySelector('[data-investor-pagination]');
        this.pagesEl = this.querySelector('[data-investor-pages]');
        this.prevBtn = this.querySelector('[data-investor-prev]');
        this.nextBtn = this.querySelector('[data-investor-next]');
        this.emptyEl = this.querySelector('[data-investor-empty]');
        this.stockEl = this.querySelector('[data-investor-stock]');

        this.pageSize = parseInt(this.dataset.pageSize, 10) || 10;
        this.noticesUrl = this.dataset.noticesUrl;
        this.stockUrl = this.dataset.stockUrl;
        this.showStock = this.dataset.showStock === 'true';

        this.prevBtn?.addEventListener('click', this.onPrevClick);
        this.nextBtn?.addEventListener('click', this.onNextClick);
        this.pagesEl?.addEventListener('click', this.onPageClick);

        this.mobileQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = this.mobileQuery.matches;
        this.mobileQuery.addEventListener('change', this.onMediaChange);

        this.loadForCurrentMode();
      }

      disconnectedCallback() {
        this.prevBtn?.removeEventListener('click', this.onPrevClick);
        this.nextBtn?.removeEventListener('click', this.onNextClick);
        this.pagesEl?.removeEventListener('click', this.onPageClick);
        this.mobileQuery?.removeEventListener('change', this.onMediaChange);
        this.teardownVirtual();
      }

      loadForCurrentMode() {
        if (this.isMobile) {
          this.loadMoreNotices();
        } else {
          this.currentPage = 1;
          this.fetchNotices(this.currentPage);
          if (this.showStock && this.stockUrl) this.fetchStock();
        }
      }

      onMediaChange(event) {
        if (event.matches === this.isMobile) return;
        this.isMobile = event.matches;
        this.teardownVirtual();
        this.allItems = [];
        this.itemHeights = [];
        this.renderStart = -1;
        this.mobileNextPage = 1;
        this.mobileHasMore = true;
        this.isFetchingMore = false;
        if (this.listEl) this.listEl.innerHTML = '';
        this.loadForCurrentMode();
      }

      buildNoticesUrl(pageIndex, pageSize = this.pageSize) {
        const url = new URL(this.noticesUrl, window.location.origin);
        url.searchParams.set('status', 'published');
        url.searchParams.set('lang_code', this.dataset.langCode || 'en-US');
        url.searchParams.set('page_size', String(pageSize));
        url.searchParams.set('page_index', String(pageIndex));
        return url.toString();
      }

      /* ---------- Desktop: paginated ---------- */

      async fetchNotices(pageIndex, scrollTop = false) {
        if (this.isLoading || !this.noticesUrl || !this.listEl) return;

        this.isLoading = true;
        this.setLoadingState(true);

        try {
          const response = await fetch(this.buildNoticesUrl(pageIndex));
          if (!response.ok) throw new Error('Failed to load investor notices');

          const payload = await response.json();
          const items = Array.isArray(payload.data) ? payload.data : [];

          this.renderList(items);
          this.renderPagination(payload);
          this.toggleEmptyState(items.length === 0);
          this.currentPage = pageIndex;
          if (scrollTop) this.scrollToTop();
        } catch (error) {
          console.error(error);
          this.renderList([]);
          this.toggleEmptyState(true);
        } finally {
          this.isLoading = false;
          this.setLoadingState(false);
        }
      }

      /* ---------- Mobile: incremental fetch + virtualize ---------- */

      // Fetches the next page and appends it. Used for both the first page
      // (on enter) and subsequent pages (as the user scrolls near the end).
      async loadMoreNotices() {
        if (this.isFetchingMore || !this.mobileHasMore || !this.noticesUrl || !this.listEl) return;

        this.isFetchingMore = true;
        this.setLoadingState(true);

        const startIndex = this.allItems.length;

        try {
          const response = await fetch(this.buildNoticesUrl(this.mobileNextPage, this.mobilePageSize));
          if (!response.ok) throw new Error('Failed to load investor notices');

          const payload = await response.json();
          const items = Array.isArray(payload.data) ? payload.data : [];
          const pageTotal = payload.page_total || 1;

          this.allItems.push(...items);
          for (let i = 0; i < items.length; i += 1) {
            this.itemHeights.push(this.estimatedRowHeight);
          }

          this.mobileNextPage += 1;
          this.mobileHasMore = this.mobileNextPage <= pageTotal && items.length > 0;

          if (startIndex === 0) {
            this.toggleEmptyState(this.allItems.length === 0);
            if (this.allItems.length) this.setupVirtual();
          } else if (items.length) {
            this.buildPrefix();
            this._needsRender = true;
            this.updateWindow();
          }
        } catch (error) {
          console.error(error);
          if (startIndex === 0) {
            this.allItems = [];
            this.itemHeights = [];
            this.renderList([]);
            this.toggleEmptyState(true);
          }
        } finally {
          this.isFetchingMore = false;
          this.setLoadingState(false);
        }
      }

      // Fetch the next page when the rendered window is within
      // `loadMoreThreshold` rows of the last loaded item.
      maybeLoadMore(start) {
        if (!this.mobileHasMore || this.isFetchingMore) return;
        const renderedEnd = Math.min(this.allItems.length, start + this.windowSize);
        if (renderedEnd >= this.allItems.length - this.loadMoreThreshold) {
          this.loadMoreNotices();
        }
      }

      setupVirtual() {
        this.renderStart = -1;
        this._needsRender = true;
        this.buildPrefix();

        if (!this._virtualActive) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onResize, { passive: true });
          this._virtualActive = true;
        }
        this.updateWindow();
      }

      teardownVirtual() {
        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = null;
        }
        if (this._virtualActive) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onResize);
          this._virtualActive = false;
        }
      }

      buildPrefix() {
        const heights = this.itemHeights;
        const prefix = new Array(heights.length + 1);
        prefix[0] = 0;
        for (let i = 0; i < heights.length; i += 1) {
          prefix[i + 1] = prefix[i] + heights[i];
        }
        this._prefix = prefix;
      }

      // Largest item index whose top offset is still above `offset`.
      findStart(offset) {
        const prefix = this._prefix;
        let lo = 0;
        let hi = prefix.length - 1;
        let ans = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (prefix[mid] <= offset) {
            ans = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }
        return Math.min(ans, Math.max(0, this.allItems.length - 1));
      }

      onScroll() {
        if (!this.isMobile || !this.allItems.length || this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this.updateWindow();
        });
      }

      onResize() {
        if (!this.isMobile) return;
        this._needsRender = true;
        this.onScroll();
      }

      updateWindow() {
        if (!this.listEl || !this.allItems.length) return;

        const rect = this.listEl.getBoundingClientRect();
        const scrolledIntoList = Math.max(0, -rect.top); // px scrolled past the list top

        let start = this.findStart(scrolledIntoList) - this.overscan;
        start = Math.max(0, Math.min(start, Math.max(0, this.allItems.length - this.windowSize)));

        this.maybeLoadMore(start);

        if (start === this.renderStart && !this._needsRender) return;
        this._needsRender = false;
        this.renderStart = start;
        this.renderWindow(start);
      }

      renderWindow(start) {
        const end = Math.min(this.allItems.length, start + this.windowSize);
        const topPad = this._prefix[start];
        const bottomPad = this._prefix[this.allItems.length] - this._prefix[end];

        const middle = this.allItems
          .slice(start, end)
          .map((item, i) => this.itemHtml(item, start + i))
          .join('');

        this.listEl.innerHTML =
          `<li class="investor-notices__spacer" aria-hidden="true" style="height:${topPad}px"></li>` +
          middle +
          `<li class="investor-notices__spacer" aria-hidden="true" style="height:${bottomPad}px"></li>`;

        this.measureWindow(start, end);
      }

      // Replace estimated heights with the real ones once the window is in the DOM.
      measureWindow(start, end) {
        const nodes = this.listEl.querySelectorAll('.investor-notices__item');
        let changed = false;

        nodes.forEach((node) => {
          const idx = Number(node.dataset.index);
          const height = node.offsetHeight;
          if (height && Math.abs(this.itemHeights[idx] - height) > 0.5) {
            this.itemHeights[idx] = height;
            changed = true;
          }
        });

        if (!changed) return;

        this.buildPrefix();
        const spacers = this.listEl.querySelectorAll('.investor-notices__spacer');
        if (spacers[0]) spacers[0].style.height = `${this._prefix[start]}px`;
        if (spacers[1]) {
          spacers[1].style.height = `${this._prefix[this.allItems.length] - this._prefix[end]}px`;
        }
      }

      /* ---------- Shared rendering ---------- */

      itemHtml(item, index) {
        const fileUrl = item.file_url?.url || '';
        const title = item.name || item.name_en || item.name_zh || '';
        const date = item.reported_at_string || item.reported_at || '';

        return `
              <li class="investor-notices__item" data-index="${index}">
                <div class="investor-notices__date">${this.escapeHtml(date)}</div>
                <div class="investor-notices__content">
                  <div class="investor-notices__title">
                    <a href="${this.escapeHtml(fileUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(title)}</a>
                  </div>
                </div>
              </li>
            `;
      }

      renderList(items) {
        this.listEl.innerHTML = items.map((item, i) => this.itemHtml(item, i)).join('');
      }

      renderPagination(payload) {
        if (!this.pagesEl || !this.prevBtn || !this.nextBtn) return;

        const pageTotal = payload.page_total || 1;
        const pageIndex = payload.page_index || 1;

        const pages = this.buildPageWindows(pageIndex, pageTotal);
        this.pagesEl.innerHTML = pages.map(p => {
          if (p === '...') {
            return `<li><span class="investor-notices__ellipsis">…</span></li>`;
          }
          const isActive = p === pageIndex;
          return `<li><button type="button" class="investor-notices__page${isActive ? ' is-active' : ''}" data-page="${p}"${isActive ? ' aria-current="page"' : ''}>${p}</button></li>`;
        }).join('');

        this.prevBtn.disabled = pageIndex <= 1;
        this.nextBtn.disabled = pageIndex >= pageTotal;
        this.paginationEl.hidden = pageTotal <= 1;
      }

      buildPageWindows(current, total) {
        if (total <= 7) {
          return Array.from({ length: total }, (_, i) => i + 1);
        }
        const delta = 2;
        const left = Math.max(2, current - delta);
        const right = Math.min(total - 1, current + delta);
        const pages = [1];
        if (left > 2) pages.push('...');
        for (let i = left; i <= right; i++) pages.push(i);
        if (right < total - 1) pages.push('...');
        pages.push(total);
        return pages;
      }

      renderStock(data) {
        const isUp = Number(data.change) > 0;
        const color = isUp ? 'var(--investor-stock-up)' : 'var(--investor-stock-down)';
        const icon = isUp ? '▲' : '▼';

        this.stockEl.innerHTML = `
          <p class="investor-notices__stock-name">
            <span>${this.escapeHtml(data.stock_name || '')}</span>
            <span>${this.escapeHtml(data.stock_code || '')}</span>
          </p>
          <p class="investor-notices__stock-price" style="color:${color}">
            <span class="investor-notices__stock-num">${this.escapeHtml(String(data.lowest_price ?? ''))}</span>
            <span class="investor-notices__stock-unit">${this.escapeHtml(data.unit || '')}</span>
          </p>
          <div class="investor-notices__stock-gains" style="color:${color}">
            <span class="investor-notices__stock-icon" aria-hidden="true">${icon}</span>
            <span>${this.escapeHtml(String(data.change ?? ''))}</span>
            <span>(${this.escapeHtml(String(data.change_p ?? ''))})</span>
          </div>
          <div class="investor-notices__stock-time">
            <p>${this.escapeHtml(data.delay_time || '')}</p>
            <p>${this.escapeHtml(data.delay_text || '')}</p>
          </div>
        `;
      }

      async fetchStock() {
        if (!this.stockEl || !this.stockUrl) return;

        try {
          const url = new URL(this.stockUrl, window.location.origin);
          url.searchParams.set('lang_code', this.dataset.langCode || 'en-US');

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('Failed to load stock info');

          const payload = await response.json();
          if (!payload?.data) return;

          this.renderStock(payload.data);
        } catch (error) {
          console.error(error);
        }
      }

      toggleEmptyState(isEmpty) {
        if (this.emptyEl) this.emptyEl.hidden = !isEmpty;
        if (this.listEl) this.listEl.hidden = isEmpty;
        if (isEmpty && this.paginationEl) this.paginationEl.hidden = true;
      }

      setLoadingState(isLoading) {
        this.classList.toggle('is-loading', isLoading);
      }

      onPrevClick() {
        if (this.isLoading || this.prevBtn?.disabled) return;
        this.fetchNotices(this.currentPage - 1, true);
      }

      onNextClick() {
        if (this.isLoading || this.nextBtn?.disabled) return;
        this.fetchNotices(this.currentPage + 1, true);
      }

      onPageClick(event) {
        const button = event.target.closest('[data-page]');
        if (!button || this.isLoading || button.classList.contains('is-active')) return;

        const page = parseInt(button.dataset.page, 10);
        if (!Number.isNaN(page)) {
          this.fetchNotices(page, true);
        }
      }

      scrollToTop() {
        // The theme uses Lenis for smooth scrolling and patches window.scrollTo.
        // Calling through the Lenis API ensures any in-progress animation is cancelled
        // and the scroll reliably reaches the top.
        if (window.__lenis) {
          window.__lenis.scrollTo(0);
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
